
import React, { useState, useEffect, useContext } from 'react';
import { Modal, notification, Button, Spin } from 'antd';
import { AlertCircle, Calendar, Clock } from 'lucide-react';
import ordersService from '../services/ordersService';
import userImpersonationService from '../services/userImpersonationService';
import CartContext from '../context/CartContext';
import userRoleUtils from '../utils/userRoleUtils';
import eventBus from '../utils/eventBus';
import { cartOrderNotesKey, readCartOrderNotes, clearCartOrderNotes } from '../utils/cartOrderNotes';

const CartTotal = ({ cart: propCart, hasInvalidDates = false, showVat = true }) => {
    const contextValue = useContext(CartContext);
    const hasContext = contextValue !== undefined;
    
    const [localCart, setLocalCart] = useState(propCart || (hasContext ? contextValue.cart : {}));
    const [user, setUser] = useState(JSON.parse(localStorage.getItem('user')));
    const [placer, setPlacer] = useState(JSON.parse(localStorage.getItem('user')));
    const [modalOpen, setModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isCollectionOnly, setIsCollectionOnly] = useState(false);
    const [orderComplete, setOrderComplete] = useState(false);
    
    // Enhanced cart state management
    useEffect(() => {
        const initialCart = propCart || (hasContext ? contextValue.cart : {});
        setLocalCart(initialCart);
        
        const unsubscribeCartUpdated = eventBus.on('cart:updated', (updatedCart) => {
            console.log('CartTotal: Cart updated event received', updatedCart);
            if (updatedCart) {
                setLocalCart(updatedCart);
            } else if (hasContext && contextValue.refreshCart) {
                contextValue.refreshCart().then(() => {
                    setLocalCart(contextValue.cart || {});
                });
            }
        });
        
        const unsubscribeCartRefresh = eventBus.on('cart:refresh', () => {
            console.log('CartTotal: Cart refresh event received');
            if (hasContext && contextValue.refreshCart) {
                contextValue.refreshCart().then(() => {
                    setLocalCart(contextValue.cart || {});
                });
            }
        });
        
        return () => {
            unsubscribeCartUpdated();
            unsubscribeCartRefresh();
        };
    }, [propCart, hasContext, contextValue]);
    
    useEffect(() => {
       
        if (propCart) {
            setLocalCart(propCart);
        } else if (hasContext && contextValue.cart) {
            setLocalCart(contextValue.cart);
        }
    }, [propCart, hasContext, contextValue?.cart]);

    useEffect(() => {
        setIsCollectionOnly(isCollectionOnlyCheck());
    }, [localCart]);
    
    const cart = localCart;

    const isCollectionOnlyCheck = () => {
        if (!cart || Object.keys(cart).length === 0) return false;

        return Object.values(cart).some(items => 
            Object.values(items).some(item => {
              //id ==120 is Collection Only product
              return parseInt(item.id) === 120;
            })
        );
    }
    
    // Calculate total from cart
    const calculateTotal = () => {
        if (!cart || Object.keys(cart).length === 0) return 0;

        let total = 0;
        
        Object.values(cart).forEach(items => {
            Object.values(items).forEach(item => {
                const price = parseFloat(item.price || 0);
                const quantity = parseFloat(item.quantity || item.qty || 0);
                total += price * quantity;
            });
        });
        
        return total;
    };
    
    const totalAmount = hasContext && contextValue.getCartTotal
        ? contextValue.getCartTotal()
        : calculateTotal();
    const displayAmount = (amount) => showVat ? amount : amount / 1.2;
    const effectiveTotalAmount = totalAmount < 25 && !isCollectionOnly ? 25 : totalAmount;
    const displaySubtotalAmount = displayAmount(totalAmount);
    const displayTotalAmount = displayAmount(effectiveTotalAmount);

    // ✅ ENHANCED: Strict validation for all properties having complete date/time
    const areAllSchedulesSet = () => {
        if (!cart || Object.keys(cart).length === 0) return false;
        
        try {
            const savedSchedule = JSON.parse(localStorage.getItem('dateIsSet') || '{}');
            
            // Check each property in cart has a COMPLETE schedule
            return Object.keys(cart).every(propertyId => {
                const schedule = savedSchedule[propertyId];
                // ✅ STRICT: Must have ALL three fields
                return schedule && 
                       schedule.order_date && 
                       schedule.arrival_time && 
                       schedule.completion_time &&
                       // Additional validation: ensure values are not empty strings
                       schedule.order_date.trim() !== '' &&
                       schedule.arrival_time.trim() !== '' &&
                       schedule.completion_time.trim() !== '';
            });
        } catch (e) {
            console.error("Error checking schedules", e);
            return false;
        }
    };

    // ✅ ENHANCED: Get detailed list of properties missing dates/times
    const getPropertiesWithMissingDateTime = () => {
        if (!cart || Object.keys(cart).length === 0) return [];
        
        try {
            const savedSchedule = JSON.parse(localStorage.getItem('dateIsSet') || '{}');
            
            return Object.keys(cart).filter(propertyId => {
                const schedule = savedSchedule[propertyId];
                // Check if ANY required field is missing or empty
                return !schedule || 
                       !schedule.order_date || 
                       !schedule.arrival_time || 
                       !schedule.completion_time ||
                       schedule.order_date.trim() === '' ||
                       schedule.arrival_time.trim() === '' ||
                       schedule.completion_time.trim() === '';
            });
        } catch (e) {
            console.error("Error checking schedules", e);
            return Object.keys(cart); // If error, assume all need dates
        }
    };

    // ✅ ENHANCED: Get property details for missing date/time warnings
    const getPropertyDetails = (propertyId) => {
        // This would typically come from your property data
        // For now, return a generic name
        return `Property ${propertyId}`;
    };

    const placeOrder = async () => {
        try {
            setIsLoading(true);
            
            // First validation - do we have a user?
            if (!user) {
                notification.error({
                    message: "Authentication Error",
                    description: "Please log in to place an order.",
                    duration: 3
                });
                return;
            }

            // Second validation - is the cart empty?
            if (!cart || Object.keys(cart).length === 0) {
                notification.error({
                    message: "Empty Cart",
                    description: "Your cart is empty. Please add items before checkout.",
                    duration: 3
                });
                return;
            }

            // ✅ ENHANCED: Strict validation for ALL properties having complete date/time
            const missingDateProperties = getPropertiesWithMissingDateTime();
            if (missingDateProperties.length > 0) {
                const propertyCount = missingDateProperties.length;
                const propertiesText = propertyCount === 1 ? 'property' : 'properties';
                const needsText = propertyCount === 1 ? 'needs' : 'need';
                
                notification.error({
                    message: "Date & Time Required",
                    description: (
                        <div>
                            <p className="mb-2">
                                <strong>{propertyCount} {propertiesText} {needsText} date and time selection:</strong>
                            </p>
                            <p className="text-sm text-gray-600 mb-3">
                                All properties must have both date and time set before checkout. 
                                Please click the calendar icon (📅) next to each property to set the required information.
                            </p>
                            <div className="bg-red-50 p-2 rounded text-xs">
                                <strong>Required for each property:</strong>
                                <ul className="mt-1 ml-4">
                                    <li>• Service Date</li>
                                    <li>• Arrival Time</li>
                                    <li>• Completion Time</li>
                                </ul>
                            </div>
                        </div>
                    ),
                    duration: 8
                });
                return;
            }
            
            // Set placer user
            let placerUser = placer;
            if (userImpersonationService.isImpersonating()) {
                const adminUser = userImpersonationService.getAdminUser();
                if (adminUser) {
                    placerUser = adminUser;
                }
            } else {
                const currentUser = JSON.parse(localStorage.getItem('user'));
                if (currentUser) {
                    placerUser = currentUser;
                }
            }
            setPlacer(placerUser);
            
            // Get order schedule from localStorage
            const orderSchedule = JSON.parse(localStorage.getItem('dateIsSet') || '{}');
            const noteStorageKey = cartOrderNotesKey();
            
            const orderData = {
                userId: user.id,
                cart: cart,
                placerId: placerUser?.id || user.id,
                orderNotes: readCartOrderNotes(Object.keys(cart), noteStorageKey),
                orderSchedule: orderSchedule || (hasContext ? contextValue.orderSchedule : {})
            };
            
            // Call API to place order
            const response = await ordersService.placeOrder(orderData);

            if (response && response.status === 'stripe_payment') {
                if (!response.checkout_url) {
                    throw new Error(response?.message || "Stripe payment link was not returned");
                }

                localStorage.removeItem('dateIsSet');
                clearCartOrderNotes(Object.keys(cart), noteStorageKey);
                setLocalCart({});
                eventBus.emit('cart:updated', {});
                window.location.href = response.checkout_url;
                return;
            }

            if (response && (response.status === 'success' || response.status === 'payment')) {
                setOrderComplete(true);
                setModalOpen(true);
                
                // Clear localStorage
                localStorage.removeItem('dateIsSet');
                clearCartOrderNotes(Object.keys(cart), noteStorageKey);
                
                // Clear local cart
                setLocalCart({});
                
                // Emit cart cleared event
                eventBus.emit('cart:updated', {});
                
                // Show thank you modal
                setTimeout(() => {
                    window.location.href = '/';
                }, 5000);
            } else {
                throw new Error(response?.message || "Failed to place order");
            }
        } catch (error) {
            console.error("Error placing order:", error);
            
            let errorMessage = 'An unexpected error occurred while placing your order.';
            let errorDetails = null;
            
            if (error.response && error.response.data) {
                const { message, missing_fields, status } = error.response.data;
                errorMessage = message || errorMessage;
                
                if (missing_fields && missing_fields.length > 0) {
                    errorDetails = `Missing fields: ${missing_fields.join(', ')}`;
                }
            }
            
            notification.error({
                message: 'Error placing order',
                description: (
                    <div>
                        <p>{errorMessage}</p>
                        {errorDetails && <p className="text-sm mt-1">{errorDetails}</p>}
                    </div>
                ),
                duration: 5
            });
        } finally {
            setIsLoading(false);
        }
    };

    const missingDateCount = getPropertiesWithMissingDateTime().length;
    const canCheckout = missingDateCount === 0 && totalAmount > 0;
    const missingDateProperties = getPropertiesWithMissingDateTime();

    return (
        cart && Object.keys(cart).length > 0 ? (
            <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
                <Modal 
                    title="Thank you for your order!" 
                    footer={null} 
                    open={modalOpen} 
                    onCancel={() => setModalOpen(false)}
                    maskClosable={false}
                    closable={orderComplete}
                >
                    <p>Your order has been placed successfully. If you have any questions, please contact us at <a href="mailto:info@golden-group.com">info@golden-group.com</a>.</p>
                    <p>You will be redirected to the home page in 5 seconds.</p>
                    <div className="flex justify-end mt-8">
                        <button
                            onClick={() => window.location.href = '/'}
                            className="px-4 py-2 bg-golden-yellow text-golden-navy text-xl font-medium rounded-lg hover:opacity-90 transition-opacity"
                        >
                            Continue
                        </button>
                    </div>
                </Modal>
                
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 lg:mb-8">Cart Total</h2>
                
                {/* ✅ ENHANCED: Detailed warning message for missing dates */}
                {missingDateCount > 0 && (
                    <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-lg p-4 sm:p-5">
                        <div className="flex items-start">
                            <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-red-500 mr-3 sm:mr-4 mt-1 flex-shrink-0 animate-pulse" />
                            <div className="flex-grow">
                                <h3 className="font-bold text-red-800 text-base sm:text-lg mb-2">
                                    Cannot Proceed to Checkout
                                </h3>
                                <p className="text-red-700 text-sm sm:text-base mb-3">
                                    <strong>{missingDateCount}</strong> {missingDateCount === 1 ? 'property needs' : 'properties need'} complete date and time information before you can place your order.
                                </p>
                                
                                <div className="bg-white rounded-lg p-3 mb-4">
                                    <p className="text-sm font-medium text-gray-800 mb-2">Required for each property:</p>
                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-4 text-xs text-gray-600">
                                        <div className="flex items-center">
                                            <Calendar className="h-3 w-3 mr-1" />
                                            Service Date
                                        </div>
                                        <div className="flex items-center">
                                            <Clock className="h-3 w-3 mr-1" />
                                            Arrival Time
                                        </div>
                                        <div className="flex items-center">
                                            <Clock className="h-3 w-3 mr-1" />
                                            Completion Time
                                        </div>
                                    </div>
                                </div>
                                
                                <p className="text-sm text-red-600 bg-red-100 p-2 rounded">
                                    <strong>Action needed:</strong> Click the calendar icon (📅) next to each property above to set the required date and time information.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Subtotal and Total */}
                {totalAmount < 25 && !isCollectionOnly ? (
                    <div className="flex justify-between items-center gap-3">
                        <span className="text-lg sm:text-xl">Subtotal</span>
                        <span className="text-lg sm:text-xl line-through text-red-500">£{displaySubtotalAmount.toFixed(2)}</span>
                    </div>
                ) : (
                    <div className="flex justify-between items-center gap-3">
                        <span className="text-lg sm:text-xl">Subtotal</span>
                        <span className="text-lg sm:text-xl font-medium">£{displaySubtotalAmount.toFixed(2)}</span>
                    </div>
                )}
                <div className="flex justify-between items-center mt-4 gap-3">
                    <span className="text-lg sm:text-xl">Total {showVat ? '(inc VAT)' : '(ex VAT)'}</span>
                    <span className="text-lg sm:text-xl font-medium">£{displayTotalAmount.toFixed(2)}</span>
                </div>
                <div className="border-t border-gray-200 mt-4 pt-4" />
                
                {/* ✅ ENHANCED: Checkout button with clear messaging */}
                <button 
                    onClick={placeOrder}
                    disabled={isLoading || !canCheckout}
                    className={`w-full py-3 sm:py-4 text-base sm:text-xl font-medium rounded-lg transition-all duration-200 mt-4 flex items-center justify-center ${
                        isLoading || !canCheckout
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed border-2 border-gray-300'
                            : 'bg-golden-yellow text-golden-navy hover:opacity-90 hover:shadow-lg border-2 border-golden-yellow'
                    }`}
                    title={!canCheckout ? 'Please set date and time for all properties before checkout' : 'Proceed to checkout'}
                >
                    {isLoading ? (
                        <>
                            <Spin className="mr-3" />
                            Processing Order...
                        </>
                    ) : !canCheckout ? (
                        <>
                            <AlertCircle className="mr-3 h-6 w-6 animate-pulse" />
                            Set All Dates & Times to Continue
                        </>
                    ) : (
                        <>
                            <Calendar className="mr-3 h-5 w-5" />
                            Complete Checkout
                        </>
                    )}
                </button>
                
                {/* ✅ ENHANCED: Additional help text when checkout is disabled */}
                {!canCheckout && missingDateCount > 0 && (
                    <div className="mt-4 text-center">
                        <p className="text-sm text-gray-600 mb-2">
                            All properties must have date and time set before checkout
                        </p>
                        <div className="bg-blue-50 p-3 rounded-lg">
                            <p className="text-xs text-blue-700">
                                <strong>Tip:</strong> Look for the calendar icon (📅) next to each property above and click it to set the required information.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        ) : (
            <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8">
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 lg:mb-8">Cart Total</h2>
                <p className="text-center text-gray-500">Your cart is empty</p>
            </div>
        )
    );
};

export default CartTotal;
